import React from 'react'
import { useNavigate } from 'react-router-dom'
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import AssignmentRounded from '@mui/icons-material/AssignmentRounded';
import PunchClockRounded from '@mui/icons-material/PunchClockRounded';
import EventNoteRounded from '@mui/icons-material/EventNoteRounded';
import StarRateRounded from '@mui/icons-material/StarRateRounded';
import BusinessCenterRounded from '@mui/icons-material/BusinessCenterRounded';
import DevicesOtherRounded from '@mui/icons-material/DevicesOtherRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import WmIcon from '../../assets/images/brand/workhub-icon.svg';
import "./Banner.scss";

const MODULES = [
  'Attendance', 'Work Reports', 'Time Sheets', 'Leaves', 'Performance',
  'Projects', 'Recruitment', 'Assets', 'Help Desk', 'Messages',
];

const Check = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path d="M5 10.5 8.5 14 15 6.5" />
  </svg>
);

/* The "hub" in WorkHub: the brand tile at the centre with the modules it
   connects around it, using the same icons as the app's sidebar so the
   picture matches what people see after they sign in. Decorative only. */
const NODES = [
  { label: 'Attendance', Icon: AccessTimeRounded },
  { label: 'Work Reports', Icon: AssignmentRounded },
  { label: 'Time Sheets', Icon: PunchClockRounded },
  { label: 'Leaves', Icon: EventNoteRounded },
  { label: 'Performance', Icon: StarRateRounded },
  { label: 'Recruitment', Icon: BusinessCenterRounded },
  { label: 'Assets', Icon: DevicesOtherRounded },
  { label: 'Help Desk', Icon: SupportAgentRounded },
];

const R = 37; // orbit radius, % of the square
const at = (i) => {
  const a = ((-90 + (360 / NODES.length) * i) * Math.PI) / 180;
  return { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) };
};

const Hub = () => (
  <div className="hero-hub" aria-hidden="true">
    <svg className="hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
      <defs>
        <linearGradient id="hub-line" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#63c8ff" />
          <stop offset="1" stopColor="#19b87a" />
        </linearGradient>
      </defs>
      <circle className="hub-orbit" cx="50" cy="50" r={R} />
      <circle className="hub-orbit hub-orbit--inner" cx="50" cy="50" r="22" />
      {NODES.map((n, i) => {
        const { x, y } = at(i);
        return <line key={n.label} className="hub-line" x1="50" y1="50" x2={x} y2={y}
          style={{ animationDelay: `${-i * 0.45}s` }} />;
      })}
    </svg>

    <div className="hub-core">
      <span className="hub-pulse" />
      <span className="hub-pulse hub-pulse--late" />
      <img src={WmIcon} alt="" />
    </div>

    {NODES.map(({ label, Icon }, i) => {
      const { x, y } = at(i);
      return (
        <div key={label} className="hub-node" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${-i * 0.7}s` }}>
          <span className="hub-node-ic"><Icon /></span>
          <small>{label}</small>
        </div>
      );
    })}
  </div>
);

const Banner = () => {
  const navigate = useNavigate();

  const seeFeatures = () => {
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="hero">
      <span className="hero-glow" aria-hidden="true" />

      <div className="container">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="hero-eyebrow"><span>HRMS</span>with daily work reporting built in</p>

            <h1 className="hero-title">
              Your people, their time and their work &mdash; <span>in one place.</span>
            </h1>

            <p className="hero-lede">
              WorkHub Manager brings attendance, daily work reports, timesheets, leave,
              performance reviews and hiring together behind one login &mdash; for
              employees, team leads and HR alike.
            </p>

            <div className="hero-actions">
              <button
                type="button"
                className="hero-btn hero-btn--primary"
                onClick={() => navigate('/login')}
              >
                Get Started
              </button>
              <button type="button" className="hero-btn hero-btn--ghost" onClick={seeFeatures}>
                Explore features
              </button>
            </div>

            <ul className="hero-proof">
              <li><Check />Role-based access</li>
              <li><Check />Works on any device</li>
              <li><Check />Excel export of work reports</li>
            </ul>
          </div>

          <div className="hero-visual">
            <Hub />
          </div>
        </div>

        <ul className="hero-modules" aria-label="Modules">
          {MODULES.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </div>
    </section>
  )
}

export default Banner
